// frontend/src/hooks/useLearningPath.ts
//
// Öğrencinin en aktif kursunu bulup, o kursun derslerini "path adımı"
// olarak gösteriyor. Per-lesson completions tablosunu kullanarak
// her dersin tamamlanma durumunu doğru şekilde yansıtır.

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuthStore } from '../store/authStore';
import { LearningPath, LearningPathStep } from '../types/courses.types';

interface ProgressWithCourseTitle {
  course_id: string;
  progress_percent: number;
  last_lesson_id: string | null;
  courses: { title: string } | null;
}

export function useLearningPath() {
  const userId = useAuthStore((s) => s.session?.user.id);
  const [path, setPath] = useState<LearningPath | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        // En son aktif olunan kursu bul
        const { data: progress, error: progressError } = await supabase
          .from('student_course_progress')
          .select('course_id, progress_percent, last_lesson_id, courses(title)')
          .eq('student_id', userId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle<ProgressWithCourseTitle>();

        if (progressError) throw progressError;

        if (!progress) {
          if (!cancelled) setPath(null);
          return;
        }

        // Kursun tüm derslerini al
        const { data: lessons, error: lessonsError } = await supabase
          .from('lessons')
          .select('id, title, order_index')
          .eq('course_id', progress.course_id)
          .order('order_index', { ascending: true });

        if (lessonsError) throw lessonsError;

        // Per-lesson completion kayıtlarını al
        const lessonIds = (lessons ?? []).map((l) => l.id);
        const { data: completions, error: completionsError } = await supabase
          .from('student_lesson_completions')
          .select('lesson_id')
          .eq('student_id', userId)
          .in('lesson_id', lessonIds);

        if (completionsError) throw completionsError;

        const completedSet = new Set((completions ?? []).map((c: any) => c.lesson_id));

        // Her ders için completed/current/locked durumunu belirle
        let foundCurrent = false;
        const steps: LearningPathStep[] = (lessons ?? []).map((lesson) => {
          if (completedSet.has(lesson.id)) {
            return {
              id: lesson.id,
              title: lesson.title,
              description: '',
              lessonId: lesson.id,
              status: 'completed' as const,
            };
          } else if (!foundCurrent) {
            foundCurrent = true;
            return {
              id: lesson.id,
              title: lesson.title,
              description: '',
              lessonId: lesson.id,
              status: 'current' as const,
            };
          } else {
            return {
              id: lesson.id,
              title: lesson.title,
              description: '',
              lessonId: lesson.id,
              status: 'locked' as const,
            };
          }
        });

        if (!cancelled) {
          setPath({
            id: progress.course_id,
            title: progress.courses?.title ?? 'Öğrenme Haritası',
            description: null,
            progressPercent: progress.progress_percent ?? 0,
            steps,
          });
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Öğrenme haritası yüklenemedi.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { path, isLoading, error };
}
